import { memo } from 'react';
import { Badge, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { ArrowRight, Boxes, RefreshCw, Sparkles, Zap } from 'lucide-react';
import IntegrationProviderLogo from './IntegrationProviderLogo.jsx';

/**
 * Marketplace Featured Card (CT-4163): a curated editorial highlight at double
 * width, above the curated grids. Matches the prototype's `.featured-card` —
 * cyan "Featured this week" eyebrow, editorial headline + blurb, capability
 * chips, a Learn more / Open CTA, and the provider logo at hero size.
 *
 * The ticket's component mapping named `HighlightCard`, but that DS component is
 * a KPI metric card (label/value/unit/icon) and cannot express this editorial
 * layout — so this is a bespoke card built from DS primitives (`Badge`, `Button`,
 * tokens). Divergence flagged for PO sign-off; the prototype is the source of truth.
 */
const CAP_META = {
  data_sync: { icon: RefreshCw, variant: 'success' },
  tool: { icon: Zap, variant: 'purple' },
  mcp: { icon: Boxes, variant: 'informative' },
};

function localizedFeaturedField(t, slug, field, fallback) {
  const key = `integrations.marketplace.featured.copy.${slug}.${field}`;
  const value = t(key);
  return value && value !== key ? value : fallback;
}

function FeaturedCard({ view, onOpen }) {
  const { t } = useTranslation('admin-studio');
  if (!view) return null;

  const headline = localizedFeaturedField(t, view.slug, 'headline', view.headline ?? view.name);
  const description = localizedFeaturedField(
    t,
    view.slug,
    'description',
    view.featuredDescription ?? view.description,
  );
  const capabilities = view.activeCapabilities ?? [];
  const isConnected = view.connectionState === 'connected';
  const ctaKey = isConnected
    ? 'integrations.marketplace.featured.open'
    : 'integrations.marketplace.featured.learnMore';

  const open = () => onOpen?.(view.slug);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t('integrations.card.openAria', { name: view.name })}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
      className="relative flex cursor-pointer flex-col items-start gap-6 overflow-hidden rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 p-7 transition-colors hover:border-cyan-500/40 sm:flex-row sm:items-center sm:gap-8"
    >
      <div className="relative z-[1] min-w-0 flex-1">
        <span className="mb-2.5 inline-flex items-center gap-1.5 rounded-lg border border-cyan-500/25 bg-cyan-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-cyan-600 dark:text-cyan-400">
          <Sparkles size={12} aria-hidden="true" />
          {t('integrations.marketplace.featured.label')}
        </span>
        <h2 className="mb-2 text-[22px] font-bold leading-tight tracking-tight text-content">
          {headline}
        </h2>
        <p className="mb-3.5 max-w-[520px] text-[13px] leading-relaxed text-content-muted">
          {description}
        </p>
        {capabilities.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {capabilities.map((cap) => {
              const meta = CAP_META[cap] ?? {};
              return (
                <Badge key={cap} variant={meta.variant ?? 'neutral'} size="small" radius="full" icon={meta.icon}>
                  {t(`integrations.card.capability.${cap}`)}
                </Badge>
              );
            })}
          </div>
        )}
        <Button
          variant="primary"
          size="sm"
          rightIcon={isConnected ? undefined : ArrowRight}
          onClick={(e) => {
            e.stopPropagation();
            open();
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {t(ctaKey)}
        </Button>
      </div>
      <div className="relative z-[1] shrink-0">
        <IntegrationProviderLogo slug={view.slug} name={view.name} size="xl" />
      </div>
    </div>
  );
}

export default memo(FeaturedCard);
